import store from '../../../store';
import { client } from '../../index';
import { authHeader, authHeaderForm } from '../../../helpers/auth-header';
import {
  setCommentsAction,
  setCommentsLoadingAction,
  setEditedCommentAction,
  setError,
  setPage,
} from '../commentsActions';
import { chunkString } from '../utils/chunkString';
import { maxCommentSize } from '../constants';
import { createArrayOfAttachments } from '../utils/createArrayOfAttachments';

export const getDocumentCommentsAction = (documentId, getMore = false) => {
  return async (dispatch) => {
    dispatch(setCommentsLoadingAction(true));

    if (getMore) {
      const { page } = store.getState().commentsChatStore;
      dispatch(setPage(page + 1));
    }
    const page = store.getState().commentsChatStore.page;
    try {
      const response = await client.get(`/Docs/notes/${documentId}`, {
        headers: authHeader(),
        params: { page },
      });
      if (getMore) {
        const comments = store.getState().commentsChatStore.comments.data;
        dispatch(setCommentsAction([...response.data, ...comments]));
      } else {
        dispatch(setCommentsAction(response.data));
      }
    } catch (e) {
      dispatch(setCommentsLoadingAction(false));
      console.error(e);
    }
  };
};

export const deleteDocumentCommentAction = (noteId) => {
  return async (dispatch) => {
    dispatch(setCommentsLoadingAction(true));
    try {
      await client.delete(`/Docs/delete_note/${noteId}`, {
        headers: authHeader(),
      });
      const comments = store.getState().commentsChatStore.comments.data;
      const newComments = comments.filter(
        (comment) => comment.noteId !== noteId
      );
      dispatch(setCommentsAction(newComments));
    } catch (e) {
      console.error(e);
    }
  };
};

export const sendDocumentCommentAction = (
  entityId,
  comment,
  attachedFiles,
  isEdit
) => {
  return async (dispatch) => {
    dispatch(setCommentsLoadingAction(true));

    if (!isEdit) {
      const arrayOfComments = chunkString(comment, maxCommentSize);

      const sentNoteIds = [];
      let lastSentNote;
      try {
        for (comment of arrayOfComments) {
          const response = await client.post(
            `/Docs/note`,
            {
              DocumentId: entityId,
              Note: comment,
            },
            { headers: authHeader() }
          );
          sentNoteIds.push(response.data);
        }

        lastSentNote = sentNoteIds[sentNoteIds.length - 1];
        sentNoteIds.pop();

        dispatch(setEditedCommentAction({ message: '' }));
      } catch (e) {
        dispatch(setCommentsLoadingAction(false));
        console.error(e);
      }
      const comments = store.getState().commentsChatStore.comments.data;
      const addedComments = [];
      if (sentNoteIds.length) {
        for (const sentNote of sentNoteIds) {
          const response = await client.get(`/Docs/note/${sentNote}`, {
            headers: authHeader(),
          });
          addedComments.push(response.data);
        }
        dispatch(setCommentsAction([...comments, ...addedComments]));
      }

      const allAttachments = [
        ...attachedFiles.images,
        ...attachedFiles.commonFiles,
      ];

      dispatch(
        sendNewAttachments(
          createArrayOfAttachments(allAttachments),
          entityId,
          lastSentNote
        )
      );
    } else {
      dispatch(editDocumentComment(comment, attachedFiles));
    }
  };
};

export const editDocumentComment = (comment, attachedFiles) => {
  return async (dispatch) => {
    const editedNoteId = store.getState().commentsChatStore.editedComment.id;
    const allAttachments = [
      ...attachedFiles.images,
      ...attachedFiles.commonFiles,
    ];
    const newAttachments = allAttachments.filter((attachment) => {
      return attachment instanceof File;
    });
    const oldAttachments = allAttachments.reduce((filtered, attachment) => {
      if (!(attachment instanceof File)) {
        filtered.push(attachment.id);
      }
      return filtered;
    }, []);
    const newAttachmentsFormDatas = createArrayOfAttachments(newAttachments);
    const documentId = store.getState().commentsChatStore.entityId;

    try {
      await client.put(
        `/Docs/note`,
        {
          NoteId: editedNoteId,
          Note: comment,
          AttachmentIds: oldAttachments,
        },
        { headers: authHeader() }
      );
    } catch (e) {
      dispatch(setCommentsLoadingAction(false));
      console.error(e);
    }

    if (newAttachmentsFormDatas.length) {
      dispatch(
        sendNewAttachments(
          newAttachmentsFormDatas,
          documentId,
          editedNoteId,
          true
        )
      );
    } else {
      dispatch(setCommentsLoadingAction(true));
      const response = await client.get(`/Docs/note/${editedNoteId}`, {
        headers: authHeader(),
      });
      const comments = store.getState().commentsChatStore.comments.data;
      const newComments = comments.map((comment) => {
        if (comment.noteId === editedNoteId) {
          return response.data;
        } else {
          return comment;
        }
      });
      dispatch(setCommentsAction(newComments));
    }
  };
};

export const sendNewAttachments = (
  attachmentsFormDatas,
  docId,
  noteId,
  isEdit = false
) => {
  return async (dispatch) => {
    await Promise.all(
      attachmentsFormDatas.map(async (formData) => {
        try {
          await client.post(
            `/Docs/${docId}/note_attachment/${noteId}`,
            formData,
            {
              headers: authHeaderForm(),
            }
          );
        } catch (e) {
          const errorMessage = `Произошла ошибка при загрузке файла ${
            formData.get('file').name
          }`;
          dispatch(setError({ isError: true, message: errorMessage }));
        }
      })
    );
    dispatch(setCommentsLoadingAction(true));

    try {
      const response = await client.get(`/Docs/note/${noteId}`, {
        headers: authHeader(),
      });
      const comments = store.getState().commentsChatStore.comments.data;
      if (isEdit) {
        const newComments = comments.map((comment) => {
          if (comment.noteId === noteId) {
            return response.data;
          } else {
            return comment;
          }
        });
        dispatch(setCommentsAction(newComments));
      } else {
        dispatch(setCommentsAction([...comments, response.data]));
      }

      dispatch(setEditedCommentAction({ message: null, attachments: [] }));
    } catch (e) {
      const errorMessage = 'Произошла ошибка при загрузке комментария';
      dispatch(setError({ isError: true, message: errorMessage }));
      dispatch(setCommentsLoadingAction(false));
    }
  };
};

export const downloadAttachedFile = (fileName, name) => {
  return async (dispatch) => {
    const docId = store.getState().commentsChatStore.entityId;
    const response = await client.get(`/Docs/attachment`, {
      headers: authHeader(),
      params: {
        docId,
        fileName,
      },
    });
    const data = Buffer.from(response.data, 'base64');
    const url = window.URL.createObjectURL(new Blob([data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', name);
    document.body.appendChild(link);
    link.click();
  };
};
